<script>
  import { modalOpen } from '../stores/modalOpen.js';
  import { onMount, onDestroy } from 'svelte';
  import { get } from 'svelte/store';
  import { _ } from 'svelte-i18n';
  import { Map, View } from 'ol';
  import { Tile as TileLayer, Vector as VectorLayer, VectorTile as VectorTileLayer } from 'ol/layer.js';
  import VectorSource from 'ol/source/Vector.js';
  import XYZ from 'ol/source/XYZ.js';
  import { Style, Fill, Stroke } from 'ol/style.js';
  import GeoJSON from 'ol/format/GeoJSON.js';
  import Feature from 'ol/Feature.js';
  import Point from 'ol/geom/Point.js';
  import { circular } from 'ol/geom/Polygon.js';
  import { transform, transformExtent, fromLonLat } from 'ol/proj';
  import { ScaleLine, defaults as defaultControls } from 'ol/control.js';
  import { defaults as defaultInteractions } from 'ol/interaction/defaults';

  import {
    mapZoom, mapMinZoom, mapMaxZoom, apiBaseUrl,
    basemapUrl, basemapStyleUrl, basemapAttribution, basemapAttributionIsExplicit,
    FALLBACK_OSM_ATTRIBUTION, basemapIsVector,
    basemapUrlIsExplicit,
    basemapCoverageBbox,
  } from '../lib/config.js';
  import {
    playgroundStyleFn,
    selectionStyle,
    equipmentLayerStyleFn,
    treeStyleFn,
    clusterTierStyleFn,
    locationDotStyleFn,
    locationAccuracyStyle,
  } from '../lib/vectorStyles.js';
  import { macroRingStyleFn, finderMacroRingStyleFn } from '../hub/macroRingStyle.js';
  import { finderClusterStyleFn, finderPlaygroundStyle } from '../lib/finderStyle.js';
  import { mappingDetail } from '../stores/mappingDetail.js';
  import { selection } from '../stores/selection.js';
  import { reportPlacing } from '../stores/reportMissing.js';
  import { mapStore } from '../stores/map.js';
  import { location } from '../stores/location.js';
  import { playgroundSourceStore } from '../stores/playgroundSource.js';
  import { activeTierStore } from '../stores/tier.js';
  import { outsideBasemapCoverage } from '../stores/basemapCoverage.js';
  import { isOutsideCoverage } from '../lib/basemapCoverage.js';
  import { effectiveFilterStore, matchesFilters } from '../stores/filters.js';
  import { overlayFeaturesStore } from '../stores/overlayLayer.js';
  import { debounce } from '../lib/utils.js';
  import { fitViewToSelection } from '../lib/playgroundHelpers.js';

  // Props: the sources are injected by the shell; Map itself never loads data.
  /** @type {VectorSource | null} - polygon-tier source (zoom > clusterMaxZoom) */
  export let playgroundSource = null;
  /** @type {VectorSource | null} - cluster-tier source (zoom ≤ clusterMaxZoom) */
  export let clusterSource = null;
  /** @type {VectorSource | null} - macro-tier source (hub-only, zoom ≤ macroMaxZoom) */
  export let macroSource = null;
  /** Backend URL used for selection — standalone passes apiBaseUrl, hub passes per-feature URL */
  export let defaultBackendUrl = apiBaseUrl;
  
  /** Hover callback: (feature, pixel) => void */
  export let onhover = null;
  /** Clear hover callback: () => void */
  export let onclearhover = null;
  /** Equipment hover callback: (feature, pixel) => void */
  export let onequipmenthover = null;
  /** Clear equipment hover callback: () => void */
  export let onclearequipmenthover = null;

  let mapContainer;
  let olMap = null;
  let backendPopup = null; // { x, y, name, offline, degraded }
  let playgroundLayer = null; // polygon tier (zoom > clusterMaxZoom) — exposed for filter reactivity
  let clusterLayer = null;    // cluster tier (zoom ≤ clusterMaxZoom) — §3
  let macroLayer = null;      // macro tier (hub-only, zoom ≤ macroMaxZoom) — P2 §5
  let macroOutlineLayer = null; // world outline shown under the macro tier — D6
  let macroOutlineLoaded = false;

  // Style for the macro world outline: a quiet land shape, deliberately far
  // from the completeness palette so it reads as context and never as data.
  const macroOutlineStyle = new Style({
    fill: new Fill({ color: 'rgba(222, 226, 220, 0.85)' }),
    stroke: new Stroke({ color: 'rgba(160, 168, 158, 0.9)', width: 0.6 }),
  });

  // Loaded on first macro tier only, so standalone deployments never fetch it.
  async function loadMacroOutline() {
    if (macroOutlineLoaded || !macroOutlineLayer) return;
    macroOutlineLoaded = true;
    try {
      const res = await fetch('/basemap/world-110m.json');
      if (!res.ok) throw new Error(`world outline ${res.status}`);
      macroOutlineLayer.getSource().addFeatures(
        new GeoJSON().readFeatures(await res.json(), {
          dataProjection: 'EPSG:4326',
          featureProjection: 'EPSG:3857',
        }),
      );
    } catch (err) {
      // macroOutlineLoaded stays true deliberately: the tier subscription
      // calls this on every transition into macro, so resetting it would
      // refetch a missing asset on each one. Context only — the macro rings
      // still render without it.
      console.warn('[spieli] macro world outline unavailable:', err);
    }
  }
  let equipmentLayer = null;  // overlay: equipment points/polygons
  let treeLayer = null;       // overlay: tree dots
  let overlayUnsubscribe = null;
  let tierUnsubscribe = null;
  let locationUnsubscribe = null;
  let selectionUnsubscribe = null;

  // Location marker state — hoisted to component scope so onDestroy can reach them.
  let locationLayer = null;
  let locationDotFeature = null;
  let locationAccuracyFeature = null;
  let locationSource = null;
  let locationAnimFrame = null;
  let lastPulseStep = -1;

  const maxAccuracyRadius = 500;

  function animateLocationDot() {
    if (locationDotFeature?.getGeometry()) {
      const step = Math.floor((Date.now() % 2000) / (2000 / 60));
      if (step !== lastPulseStep) {
        lastPulseStep = step;
        locationDotFeature.changed();
      }
      locationAnimFrame = requestAnimationFrame(animateLocationDot);
    } else {
      locationAnimFrame = null;
    }
  }

  function startLocationAnimation() {
    if (!locationAnimFrame) locationAnimFrame = requestAnimationFrame(animateLocationDot);
  }

  function stopLocationAnimation() {
    if (locationAnimFrame) {
      cancelAnimationFrame(locationAnimFrame);
      locationAnimFrame = null;
    }
  }

  onMount(async () => {
    // The shell owns the sources; fall back to empty ones so the map still
    // renders in degraded paths (e.g. tests that mount Map without a parent).
    const polygonSrc = playgroundSource ?? new VectorSource();
    const clusterSrc = clusterSource    ?? new VectorSource();
    const macroSrc   = macroSource      ?? new VectorSource();

    // All three tier layers start hidden; the activeTierStore subscription
    // below reveals exactly one once the orchestrator has chosen a tier.
    playgroundLayer = new VectorLayer({
      source: polygonSrc,
      zIndex: 10,
      style: playgroundStyleFn,
      visible: false,
    });
    clusterLayer = new VectorLayer({
      source: clusterSrc,
      zIndex: 12,
      style: clusterTierStyleFn,
      visible: false,
    });
    // Macro tier sits above the cluster layer (zIndex 14) so any future
    // overlap during a tier transition keeps the macro rings on top.
    macroLayer = new VectorLayer({
      source: macroSrc,
      zIndex: 14,
      style: macroRingStyleFn,
      visible: false,
    });

    // Detailed-coverage notice (#847). The rule lives in
    // lib/basemapCoverage.js and is unit-tested there; this only feeds it the
    // view state and publishes the answer.
    function updateBasemapCoverage(v) {
      const centre = v.getCenter();
      outsideBasemapCoverage.set(isOutsideCoverage(
        basemapCoverageBbox,
        centre ? transform(centre, 'EPSG:3857', 'EPSG:4326') : null,
        v.getZoom(),
      ));
    }

    // Basemap: a vector style when one is configured, an XYZ raster otherwise.
    // Both sit at the bottom of the stack; the tier layers keep their zIndex.
    // applyStyle() styles a layer we own rather than apply() taking over the
    // map, so the layer ordering and the activeTierStore wiring below are
    // untouched by the basemap choice.
    const rasterBasemapSource = () => new XYZ({
      url: basemapUrl,
      attributions: basemapAttribution,
    });

    // Explicit zIndex 0 so the macro outline at -1 is reliably behind it.
    const basemap = basemapIsVector
      ? new VectorTileLayer({ declutter: true, zIndex: 0 })
      : new TileLayer({ source: rasterBasemapSource(), zIndex: 0 });

    if (basemapIsVector) {
      // Dynamically imported: the library adds ~310 kB raw / ~50 kB gzipped.
      // Vector is now the default, so most deployments do load it — but on a
      // second round trip after first paint rather than in the main bundle,
      // and a raster-configured deployment still never fetches it at all.
      import('ol-mapbox-style')
        .then(({ applyStyle }) => applyStyle(basemap, basemapStyleUrl, {
          // ol-mapbox-style otherwise resolves webfonts from cdn.jsdelivr.net
          // at runtime, which would add a third-party host to every page load
          // — the exact leak this basemap work exists to remove, reintroduced
          // by the rendering library rather than by the tiles. Pointing it at
          // a same-origin path keeps it local; if no font is shipped there the
          // request 404s and labels fall back to a system font, which costs
          // some typographic fidelity and contacts nobody.
          webfonts: '/basemap/fonts/{font-family}/{fontweight}{-fontstyle}.css',
        }))
        .then(() => {
          // Attribution belongs to the SOURCE, not the layer — OpenLayers
          // resolves it via layer.getSource().getAttributions(), so setting it
          // as a layer option silently does nothing.
          //
          // Only override when the OPERATOR named a credit. A tile server
          // declares its own attribution in its TileJSON and that is
          // authoritative: tiles.openfreemap.org and a self-hosted tileserver
          // return different, individually correct values. Overwriting it with
          // a compiled-in default credits whoever that default names, which is
          // how a map built from our own Planetiler tiles came to display
          // "© OpenFreeMap".
          const source = basemap.getSource();
          if (!source) return;
          if (basemapAttributionIsExplicit) {
            source.setAttributions(basemapAttribution);
            return;
          }
          // Floor. Letting the source speak for itself is right, but a server
          // that declares nothing would otherwise leave OSM-derived tiles with
          // NO credit — an empty attribution control, which is a licence
          // breach rather than the merely-wrong credit this replaced. Common
          // enough to matter: a stock tileserver-gl config has no attribution
          // key, and neither does this repo's own CI stub.
          if (!source.getAttributions()) {
            source.setAttributions(FALLBACK_OSM_ATTRIBUTION);
            console.warn(
              '[spieli] The basemap tile server declares no attribution, so a ' +
              'bare OpenStreetMap credit is being shown. If its tileset needs ' +
              'more than that (OpenMapTiles output is CC-BY, for example), set ' +
              'BASEMAP_ATTRIBUTION.',
            );
          }
        })
        .catch(err => {
          console.error('[spieli] basemap style failed to load:', err);
          // Fall back to the raster source ONLY when the operator configured
          // one. There is no raster default, so with nothing configured there
          // is nothing safe to reach for and the map renders without a
          // basemap — rather than silently substituting some third party the
          // operator never chose.
          if (!basemapUrlIsExplicit) return;
          try {
            // NOTE: basemapAttribution was written for the style provider, so
            // the fallback credits it over the raster provider's tiles. Both
            // are operator-configured in the only case this can fire (style
            // and raster both set), and a single attribution string cannot
            // describe two providers — tracked in #830 rather than silently
            // rendering the wrong credit forever.
            olMap.removeLayer(basemap);
            olMap.addLayer(new TileLayer({ source: rasterBasemapSource(), zIndex: 0 }));
          } catch (fallbackErr) {
            console.error('[spieli] raster basemap fallback failed:', fallbackErr);
          }
        });
    }

    const view = new View({
      center: transform([10.5, 51.2], 'EPSG:4326', 'EPSG:3857'), // Germany fallback
      zoom: mapZoom,
      minZoom: mapMinZoom,
      maxZoom: mapMaxZoom,
      constrainResolution: true,
    });

    olMap = new Map({
      target: mapContainer,
      layers: [basemap, playgroundLayer, clusterLayer, macroLayer],
      view,
      // Disable default zoom/rotate controls for cleaner UI like Google Maps
      controls: defaultControls({ 
        zoom: false, 
        rotate: false,
        attribution: true 
      }).extend([
        new ScaleLine({ units: 'metric', className: 'custom-scale-line' })
      ]),
      interactions: defaultInteractions({ altShiftDragRotate: false, pinchRotate: false }),
    });

    mapStore.set(olMap);

    // Subscribe to overlay features store — rebuild equipment/tree layers on each change
    overlayUnsubscribe = overlayFeaturesStore.subscribe(({ equipment, trees }) => {
      const shouldBeVisible = $activeTierStore === 'polygon';
      
      if (equipmentLayer) { olMap.removeLayer(equipmentLayer); equipmentLayer = null; }
      if (treeLayer)      { olMap.removeLayer(treeLayer);      treeLayer      = null; }

      if (equipment.length > 0) {
        const src = new VectorSource();
        const olFeatures = new GeoJSON().readFeatures(
          { type: 'FeatureCollection', features: equipment },
          { dataProjection: 'EPSG:4326', featureProjection: 'EPSG:3857' }
        );
        src.addFeatures(olFeatures);
        equipmentLayer = new VectorLayer({
          source: src,
          zIndex: 20,
          style: equipmentLayerStyleFn,
          properties: { kind: 'overlay' },
          visible: shouldBeVisible,
        });
        olMap.addLayer(equipmentLayer);
      }

      if (trees.length > 0) {
        const src = new VectorSource();
        src.addFeatures(new GeoJSON().readFeatures(
          { type: 'FeatureCollection', features: trees },
          { dataProjection: 'EPSG:4326', featureProjection: 'EPSG:3857' }
        ));
        treeLayer = new VectorLayer({ 
          source: src, 
          zIndex: 15, 
          style: treeStyleFn,
          visible: shouldBeVisible
        });
        olMap.addLayer(treeLayer);
      }
    });

    locationUnsubscribe = location.subscribe(loc => {
      if (!locationLayer) {
        if (loc && Number.isFinite(loc.lat) && Number.isFinite(loc.lon)) {
          locationSource = new VectorSource();
          locationAccuracyFeature = new Feature();
          locationAccuracyFeature.setStyle(locationAccuracyStyle);
          locationDotFeature = new Feature({
            geometry: new Point(fromLonLat([loc.lon, loc.lat])),
          });
          locationDotFeature.setStyle(locationDotStyleFn);
          const clampedAccuracy = Math.min(loc.accuracy ?? 0, maxAccuracyRadius);
          if (clampedAccuracy > 0) {
            locationAccuracyFeature.setGeometry(
              circular([loc.lon, loc.lat], clampedAccuracy).transform('EPSG:4326', 'EPSG:3857')
            );
          }
          locationSource.addFeatures([locationAccuracyFeature, locationDotFeature]);
          locationLayer = new VectorLayer({
            source: locationSource,
            zIndex: 30,
          });
          olMap.addLayer(locationLayer);
          startLocationAnimation();
        }
      } else {
        if (loc && Number.isFinite(loc.lat) && Number.isFinite(loc.lon)) {
          locationLayer.setVisible(true);
          locationDotFeature.getGeometry().setCoordinates(fromLonLat([loc.lon, loc.lat]));
          const clampedAccuracy = Math.min(loc.accuracy ?? 0, maxAccuracyRadius);
          if (clampedAccuracy > 0) {
            locationAccuracyFeature.setGeometry(
              circular([loc.lon, loc.lat], clampedAccuracy).transform('EPSG:4326', 'EPSG:3857')
            );
          } else {
            locationAccuracyFeature.setGeometry(null);
          }
          startLocationAnimation();
        } else {
          locationLayer.setVisible(false);
          stopLocationAnimation();
        }
      }
    });

    // Zoom level at which the current playground was selected; null when nothing selected.
    // Used to auto-clear selection when the user zooms out 2+ levels.
    let selectionZoom = null;
    // Keep selectionZoom in sync with the selection store. The panel close
    // button, ESC, and mobile "back to map" all clear the selection from
    // outside Map.svelte; without this, selectionZoom would stay stale and a
    // later empty-map click could zoom out with nothing selected.
    selectionUnsubscribe = selection.subscribe(() => {
      // Reset on every selection change. The map-click fit re-stamps
      // selectionZoom once its own fit settles; Nearby/deeplink selections leave
      // it null so their programmatic fit can't be mistaken for a user zoom-out
      // and auto-clear the just-made selection (#684).
      selectionZoom = null;
    });
    // Steps to zoom out when deselecting via empty-map click — matches the
    // desktop panel-close gesture (PlaygroundPanel DESKTOP_CLOSE_ZOOM_OUT).
    const DESELECT_ZOOM_OUT = 4;
    // Once up front too: a deployment can load straight into an uncovered
    // area, and without this the notice would only appear after the first pan.
    updateBasemapCoverage(view);
    olMap.on('moveend', () => {
      updateBasemapCoverage(view);
      if (selectionZoom !== null && view.getZoom() <= selectionZoom - DESELECT_ZOOM_OUT) {
        selection.clear();
        selectionZoom = null;
      }
      // P1: popup is anchored to a click pixel; dismiss it on any pan/zoom so it
      // doesn't drift away from its ring.
      backendPopup = null;
    });

    // Click handler: tier-aware.
    //  - Polygon tier: select + fit-to-extent (existing behaviour).
    //  - Cluster tier (§4.5): zoom in ~2 levels toward the cluster centre;
    //    a single-child cluster (count === 1) at high zoom transitions
    //    naturally into the polygon tier.
    //  - Macro tier (P2 §5): fit-to-bbox of the clicked backend; the
    //    orchestrator's debounced moveend will land in cluster or polygon
    //    tier and trigger a fan-out scoped to that backend.
    //  - Empty space: clear selection.
    olMap.on('click', (evt) => {
      // While a missing-playground report is being placed, a tap moves the
      // map under the crosshair; it must not open a panel over it (#1000).
      if (get(reportPlacing)) return;
      // A tap on one of the visitor's own report pins belongs to
      // ReportMissingFlow alone: no selection, no empty-map deselect.
      const ownPinHit = olMap.forEachFeatureAtPixel(evt.pixel, () => true, {
        layerFilter: (l) => l.get('spieliOwnNotes') === true,
        hitTolerance: 6,
      });
      if (ownPinHit) return;
      const polygonHit = olMap.forEachFeatureAtPixel(evt.pixel, (f) => f, {
        layerFilter: (l) => l === playgroundLayer,
        hitTolerance: 10,
      });
      if (polygonHit) {
        const backendUrl = polygonHit.get('_backendUrl') ?? defaultBackendUrl;
        selection.select(polygonHit, backendUrl);
        selectionZoom = null;
        // Shared fit (responsive padding + polygon-tier floor); stamp the
        // settled zoom so an empty-map click later zooms out to deselect.
        fitViewToSelection(view, polygonHit.getGeometry().getExtent(), (v) => {
          selectionZoom = v.getZoom();
        });
        return;
      }
      // Standalone pitches (#934): only layers the shell marks `selectable`
      // (the pitch layer, not the equipment overlay of a selected
      // playground), and only the pitch itself, not benches on that layer.
      // After the playground test, so a playground keeps its clicks.
      const pitchHit = olMap.forEachFeatureAtPixel(
        evt.pixel,
        (f) => (f.get('leisure') === 'pitch' ? f : undefined),
        { layerFilter: (l) => l.get('selectable') === true },
      );
      if (pitchHit) {
        // Hub pitches carry their backend; standalone ones use this instance's.
        selection.select(pitchHit, pitchHit.get('_backendUrl') ?? defaultBackendUrl, 'pitch');
        selectionZoom = null;
        fitViewToSelection(view, pitchHit.getGeometry().getExtent(), (v) => {
          selectionZoom = v.getZoom();
        });
        return;
      }
      const clusterHit = olMap.forEachFeatureAtPixel(evt.pixel, (f) => f, {
        layerFilter: (l) => l === clusterLayer,
      });
      if (clusterHit) {
        const center = clusterHit.getGeometry().getCoordinates();
        view.animate({
          center,
          zoom: Math.min((view.getZoom() ?? 0) + 2, view.getMaxZoom?.() ?? 21),
          duration: 400,
        });
        return;
      }
      const macroHit = olMap.forEachFeatureAtPixel(evt.pixel, (f) => f, {
        layerFilter: (l) => l === macroLayer,
      });
      if (macroHit) {
        const isOffline  = macroHit.get('_offline');
        const isDegraded = macroHit.get('_degraded');
        if (isOffline || isDegraded) {
          backendPopup = {
            x:        evt.pixel[0],
            y:        evt.pixel[1],
            name:     macroHit.get('_name') ?? 'Backend',
            offline:  isOffline,
            degraded: isDegraded,
          };
          return;
        }
        backendPopup = null;
        const bbox4326 = macroHit.get('_bbox');
        if (Array.isArray(bbox4326) && bbox4326.length === 4) {
          view.fit(transformExtent(bbox4326, 'EPSG:4326', 'EPSG:3857'), {
            padding: [40, 40, 40, 420],
            duration: 400,
            maxZoom: mapMaxZoom,
          });
        }
        return;
      }
      backendPopup = null;
      // Align deselect-by-empty-click with the panel-close gesture: if a
      // playground is selected, zoom out a few steps too. selectionZoom is
      // kept non-null only while selected (see the selection.subscribe above),
      // so this guard also prevents a bare click on the open map from moving
      // the view when nothing is selected.
      if (selectionZoom !== null) {
        const z = view.getZoom();
        if (Number.isFinite(z)) {
          view.animate({ zoom: Math.max(z - DESELECT_ZOOM_OUT, mapMinZoom), duration: 400 });
        }
        selectionZoom = null;
      }
      selection.clear();
    });

    // Pointer cursor on hover + hover preview callback
    let lastHoverFeature = null;
    let lastEquipHoverFeature = null;
    const debouncedHover = debounce((feature, pixel) => {
      if (onhover) onhover(feature, pixel);
    }, 100);

    olMap.on('pointermove', (evt) => {
      // Overlay hit-test: any layer tagged with `kind: 'overlay'` participates
      // (equipment points, standalone pitches contributed by the shell).
      const overlayHit = olMap.forEachFeatureAtPixel(evt.pixel, f => f, {
        layerFilter: l => l.get('kind') === 'overlay',
      });
      const playHit = olMap.forEachFeatureAtPixel(evt.pixel, f => f, {
        layerFilter: l => l === playgroundLayer,
        hitTolerance: 10,
      });
      // Cluster tier hits get the pointer cursor too — click-to-zoom is
      // wired for them and users need the affordance.
      const clusterHit = olMap.forEachFeatureAtPixel(evt.pixel, f => f, {
        layerFilter: l => l === clusterLayer,
      });
      // Macro tier rings: click → fit-to-bbox; same affordance as clusters.
      const macroHit = olMap.forEachFeatureAtPixel(evt.pixel, f => f, {
        layerFilter: l => l === macroLayer,
      });

      mapContainer.style.cursor = (overlayHit || playHit || clusterHit || macroHit) ? 'pointer' : '';

      // Overlay hover takes priority
      if (overlayHit !== lastEquipHoverFeature) {
        lastEquipHoverFeature = overlayHit;
        if (overlayHit) {
          if (onequipmenthover) onequipmenthover(overlayHit, evt.pixel);
          if (lastHoverFeature) { lastHoverFeature = null; if (onclearhover) onclearhover(); }
        } else {
          if (onclearequipmenthover) onclearequipmenthover();
        }
      }

      // Playground hover (only when not hovering an overlay feature)
      if (!overlayHit) {
        if (playHit && playHit !== lastHoverFeature) {
          lastHoverFeature = playHit;
          debouncedHover(playHit, evt.pixel);
        } else if (!playHit && lastHoverFeature) {
          lastHoverFeature = null;
          debouncedHover.cancel();
          if (onclearhover) onclearhover();
        }
      }
    });

    mapContainer.addEventListener('pointerleave', () => {
      debouncedHover.cancel();
      if (lastEquipHoverFeature) {
        lastEquipHoverFeature = null;
        if (onclearequipmenthover) onclearequipmenthover();
      }
      if (lastHoverFeature) {
        lastHoverFeature = null;
        mapContainer.style.cursor = '';
        if (onclearhover) onclearhover();
      }
    });

    // Macro-tier world outline (zIndex 1, just above the basemap). The
    // basemap tileset covers only the federation's own countries, so at
    // macroMaxZoom everything outside them would be blank — the same silent
    // void that disqualified a Germany-only provider. This fills it in from a
    // bundled Natural Earth extract, with no network request to a third party.
    // Fetched lazily on first macro tier so it never costs standalone users.
    // zIndex -1: BELOW the basemap, not above it. This is a backdrop that
    // shows through where the tileset has no coverage, not an overlay. Drawn
    // on top it washes the basemap out everywhere the basemap does render —
    // verified by A/B screenshots at the macro tier, where roads, labels and
    // terrain detail all disappeared behind it.
    macroOutlineLayer = new VectorLayer({
      source: new VectorSource(),
      zIndex: -1,
      style: macroOutlineStyle,
      visible: false,
    });
    olMap.addLayer(macroOutlineLayer);

    // Publish the polygon source once. Consumers that need to know whether
    // the polygon tier is *visible* read activeTierStore; consumers that
    // only need to read or hydrate features (AppShell
    // hash restore) get a stable reference at any tier.
    playgroundSourceStore.set(polygonSrc);

    // Tier-driven layer visibility. `tier === null` means the orchestrator
    // hasn't run yet — keep all three tier layers hidden.
    tierUnsubscribe = activeTierStore.subscribe(tier => {
      if (!playgroundLayer) return;
      if (tier === null) {
        playgroundLayer.setVisible(false);
        clusterLayer.setVisible(false);
        macroLayer.setVisible(false);
        if (equipmentLayer) equipmentLayer.setVisible(false);
        if (treeLayer) treeLayer.setVisible(false);
        return;
      }
      // P2: dismiss the popup whenever we leave macro tier.
      if (tier !== 'macro') backendPopup = null;
      playgroundLayer.setVisible(tier === 'polygon');
      clusterLayer.setVisible(tier === 'cluster');
      macroLayer.setVisible(tier === 'macro');
      // Only meaningful under a vector basemap, whose background is not
      // painted by applyStyle, so the outline shows through where the tileset
      // has no coverage. Raster tiles are opaque PNGs at zIndex 0, so an
      // outline beneath them can never be seen — and fetching it would be a
      // pointless 164 kB on every macro entry.
      if (macroOutlineLayer && basemapIsVector) {
        macroOutlineLayer.setVisible(tier === 'macro');
        if (tier === 'macro') loadMacroOutline();
      }
      // Equipment and tree overlays only visible in polygon tier
      if (equipmentLayer) equipmentLayer.setVisible(tier === 'polygon');
      if (treeLayer) treeLayer.setVisible(tier === 'polygon');
    });
  });

  onDestroy(() => {
    if (overlayUnsubscribe) overlayUnsubscribe();
    if (tierUnsubscribe) tierUnsubscribe();
    if (locationUnsubscribe) locationUnsubscribe();
    if (selectionUnsubscribe) selectionUnsubscribe();
    stopLocationAnimation();
    if (olMap) {
      olMap.setTarget(undefined);
      mapStore.set(null);
    }
    playgroundSourceStore.set(null);
  });

  // Re-style the playground layer whenever filters, mapping detail or (in
  // finder mode, which outlines the selected polygon) the selection change.
  // Playgrounds only: a selected pitch (#934) may share its numeric osm_id
  // with a playground, which would then be outlined instead.
  $: selectedOsmId = $selection.kind === 'playground' ? ($selection.feature?.get('osm_id') ?? null) : null;
  $: if (playgroundLayer) {
    const filters = $effectiveFilterStore;
    const detail = $mappingDetail;
    const selectedId = selectedOsmId;
    playgroundLayer.setStyle(feature => {
      if (!matchesFilters(feature.getProperties(), filters)) return null;
      return detail ? playgroundStyleFn(feature) : finderPlaygroundStyle(feature, selectedId);
    });
  }

  // Cluster and macro tiers: completeness rings with mapping detail on, the
  // neutral finder markers with it off (#924).
  $: if (clusterLayer) clusterLayer.setStyle($mappingDetail ? clusterTierStyleFn : finderClusterStyleFn);
  $: if (macroLayer) macroLayer.setStyle($mappingDetail ? macroRingStyleFn : finderMacroRingStyleFn);
</script>

<!-- Not while a dialog is open: that Escape belongs to the dialog, and the popup
     behind it (inert, #758) would vanish unseen. -->
<svelte:window on:keydown={(e) => { if (e.key === 'Escape' && backendPopup && !$modalOpen) backendPopup = null; }} />
<!-- tabindex is required, not decorative: OpenLayers attaches its KeyboardPan/KeyboardZoom
     listeners to this exact element (via `target: mapContainer`), so it must be focusable
     for those interactions to activate at all. -->
<!-- svelte-ignore a11y_no_noninteractive_tabindex -->
<div bind:this={mapContainer} class="map-container" role="region" aria-label={$_('map.ariaLabel')} tabindex="0">
  {#if backendPopup}
    <div
      class="backend-popup"
      style="left: {backendPopup.x}px; top: {backendPopup.y}px"
      role="status"
      aria-label="Backend status"
      aria-live="polite"
    >
      <button class="backend-popup-close" on:click={() => backendPopup = null} aria-label="Close">×</button>
      <strong class="backend-popup-name">{backendPopup.name}</strong>
      {#if backendPopup.offline}
        <p class="backend-popup-msg">Backend ist nicht erreichbar. Möglicherweise liegt ein Konfigurationsproblem im Hub oder beim Backend vor.</p>
        <a
          class="backend-popup-link"
          href="https://mfuhrmann.github.io/spieli/ops/troubleshooting/#hub-shows-all-backends-as-red--unreachable"
          target="_blank"
          rel="noopener noreferrer"
        >Troubleshooting →</a>
      {:else}
        <p class="backend-popup-msg">Backend erreichbar, aber keine Spielplatzdaten vorhanden.</p>
        <a
          class="backend-popup-link"
          href="https://mfuhrmann.github.io/spieli/ops/troubleshooting/#hub-shows-a-backend-as-reachable-but-with-0-playgrounds"
          target="_blank"
          rel="noopener noreferrer"
        >Troubleshooting →</a>
      {/if}
    </div>
  {/if}
</div>

<style>
  .map-container {
    width: 100%;
    height: 100%;
    position: absolute;
    inset: 0;
  }
  .map-container:focus-visible {
    outline: 2px solid var(--color-ring);
    outline-offset: -2px;
  }

  .backend-popup {
    position: absolute;
    z-index: 200;
    transform: translate(-50%, calc(-100% - 12px));
    background: white;
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0,0,0,0.18);
    padding: 12px 14px 10px;
    width: 240px;
    font-size: 13px;
    line-height: 1.4;
    pointer-events: all;
  }

  .backend-popup-close {
    position: absolute;
    top: 6px;
    right: 8px;
    background: none;
    border: none;
    font-size: 16px;
    line-height: 1;
    color: #6b7280;
    cursor: pointer;
    padding: 0 2px;
  }

  .backend-popup-close:hover {
    color: #111827;
  }

  .backend-popup-name {
    display: block;
    font-size: 14px;
    margin-bottom: 5px;
    padding-right: 16px;
    color: #111827;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .backend-popup-msg {
    margin: 0 0 8px;
    color: #374151;
  }

  .backend-popup-link {
    color: #2563eb;
    text-decoration: none;
    font-weight: 500;
  }

  .backend-popup-link:hover {
    text-decoration: underline;
  }

  /* Custom scale line styling - bottom left, minimal */
  :global(.custom-scale-line) {
    position: absolute;
    bottom: 24px;
    left: 10px;
    background: rgba(255, 255, 255, 0.85);
    border-radius: 4px;
    padding: 2px 6px;
    font-size: 11px;
    color: #333;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  }

  :global(.custom-scale-line .ol-scale-line-inner) {
    border: 1px solid #333;
    border-top: none;
    color: #333;
    font-size: 11px;
    text-align: center;
  }

  /* Attribution styling - bottom right, minimal */
  :global(.ol-attribution) {
    position: absolute;
    bottom: 4px;
    right: 4px;
    background: rgba(255, 255, 255, 0.7);
    padding: 2px 6px;
    border-radius: 3px;
    font-size: 10px;
  }

  :global(.ol-attribution ul) {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  :global(.ol-attribution li) {
    display: inline;
  }

  :global(.ol-attribution button) {
    display: none;
  }
</style>
